import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaShoppingBag } from 'react-icons/fa';
import { validatePromoCode, calculateFlatDiscount } from '../../utils/pricing';
import './CartPage.css';

const CartPage = () => {
  const [cartItems, setCartItems] = useState([]);
  const [promoCode, setPromoCode] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null); // { code, discount }
  const [settings, setSettings] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    loadCart();
    loadSettings();
    window.addEventListener('cartUpdated', loadCart);
    return () => window.removeEventListener('cartUpdated', loadCart);
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem('appliedPromo');
    if (saved && cartItems.length > 0 && Object.keys(settings).length > 0) {
      try {
        const parsed = JSON.parse(saved);
        const result = validatePromoCode(parsed.code, subtotal, cartItems, settings);
        if (result.valid) {
          const applied = { code: parsed.code, discount: result.discount };
          setAppliedPromo(applied);
          localStorage.setItem('appliedPromo', JSON.stringify(applied));
        } else {
          // Code no longer valid (disabled, changed, etc.) — clear it
          // rather than keep showing a stale "applied" state.
          setAppliedPromo(null);
          localStorage.removeItem('appliedPromo');
        }
      } catch (e) { }
    }
  }, [settings, cartItems]);

  const loadCart = () => {
    const cart = JSON.parse(localStorage.getItem('cart')) || [];
    setCartItems(cart);
  };

  const loadSettings = async () => {
    try {
      const response = await fetch('https://demo-site.creativepulse.pk/demo-api/public/get-settings.php');
      const data = await response.json();
      if (data.success && data.settings) {
        setSettings(data.settings);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const updateQuantity = (id, newQuantity) => {
    if (newQuantity < 1) return;
    const updatedCart = cartItems.map(item =>
      item.id === id ? { ...item, quantity: newQuantity } : item
    );
    localStorage.setItem('cart', JSON.stringify(updatedCart));
    setCartItems(updatedCart);
    window.dispatchEvent(new Event('cartUpdated'));
  };

  const removeItem = (id) => {
    const updatedCart = cartItems.filter(item => item.id !== id);
    localStorage.setItem('cart', JSON.stringify(updatedCart));
    setCartItems(updatedCart);
    window.dispatchEvent(new Event('cartUpdated'));
  };

  const clearCart = () => {
    if (window.confirm('Are you sure you want to clear your cart?')) {
      localStorage.removeItem('cart');
      setCartItems([]);
      window.dispatchEvent(new Event('cartUpdated'));
    }
  };

  const handleApplyPromo = () => {
    if (!promoCode.trim()) return;
    const result = validatePromoCode(promoCode, subtotal, cartItems, settings);
    if (result.valid) {
      const applied = { code: promoCode.trim(), discount: result.discount };
      setAppliedPromo(applied);
      localStorage.setItem('appliedPromo', JSON.stringify(applied));
      toast.success(result.message);
    } else {
      toast.error(result.message);
    }
  };

  const handleRemovePromo = () => {
    setAppliedPromo(null);
    localStorage.removeItem('appliedPromo');
    setPromoCode('');
  };

  const subtotal = cartItems.reduce((total, item) => total + item.price * item.quantity, 0);
  const promoDiscount = appliedPromo?.discount || 0;
  const flatDiscount = calculateFlatDiscount(subtotal, settings);
  const discount = promoDiscount + flatDiscount;
  // const discount = appliedPromo?.discount || 0;
  // No shipping shown here — depends on the delivery city, which is only
  // collected on the Checkout page. This is subtotal minus discount only.
  const total = Math.max(0, subtotal - discount);
  const totalItems = cartItems.reduce((total, item) => total + item.quantity, 0);

  if (cartItems.length === 0) {
    return (
      <div className="empty-cart">
        <FaShoppingBag className="empty-cart-icon" />
        <h2>Your cart is empty</h2>
        <p>Looks like you haven't added any items to your cart yet.</p>
        <Link to="/shop" className="continue-shopping-btn">Continue Shopping</Link>
      </div>
    );
  }

  return (
    <div className="cart-page">
      <div className="cart-container">
        <div className="cart-breadcrumb">
          <Link to="/">Home</Link> <span>&gt;</span> <span>Cart</span>
        </div>

        <h1>Your Cart</h1>
        <p className="cart-subtitle">Review your items before proceeding to checkout.</p>

        <div className="cart-content">
          <div className="cart-items">
            <div className="cart-header">
              <div>Product</div>
              <div>Quantity</div>
              <div>Price</div>
            </div>

            {cartItems.map(item => (
              <div key={item.id} className="cart-item">
                <div className="cart-item-product">
                  <img src={item.image || 'https://via.placeholder.com/80'} alt={item.name} />
                  <div>
                    <Link to={`/product/${item.id}`} className="cart-item-name">
                      {item.name}
                    </Link>
                    {(item.color || item.size) && (
                      <p className="cart-item-variant">
                        {[item.color ? `Color: ${item.color}` : null, item.size ? `Size: ${item.size}` : null].filter(Boolean).join(' · ')}
                      </p>
                    )}
                  </div>
                </div>
                <div className="cart-item-quantity">
                  <button onClick={() => updateQuantity(item.id, item.quantity - 1)}>−</button>
                  <span>{item.quantity}</span>
                  <button onClick={() => updateQuantity(item.id, item.quantity + 1)}>+</button>
                </div>
                <div className="cart-item-price-cell">
                  <span>PKR {(item.price * item.quantity).toLocaleString()}</span>
                  <button className="cart-item-remove" onClick={() => removeItem(item.id)}>✕</button>
                </div>
              </div>
            ))}

            <div className="cart-bottom-links">
              <Link to="/shop" className="continue-shop-link">← Continue Shopping</Link>
              <button className="clear-cart-link" onClick={clearCart}>Clear Cart</button>
            </div>
          </div>

          <div className="cart-summary">
            <h3>Order Summary</h3>

            {appliedPromo ? (
              <div className="promo-row">
                <span style={{ flex: 1, fontSize: '0.85rem' }}>
                  Code <strong>{appliedPromo.code}</strong> applied
                </span>
                <button onClick={handleRemovePromo}>Remove</button>
              </div>
            ) : (
              <div className="promo-row">
                <input
                  type="text"
                  placeholder="Enter promo code"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value)}
                />
                <button onClick={handleApplyPromo}>Apply</button>
              </div>
            )}

            <div className="summary-row">
              <span>Subtotal</span>
              <span>PKR {subtotal.toLocaleString()}</span>
            </div>
            {flatDiscount > 0 && (
              <div className="summary-row">
                <span>Flat Discount ({settings.flat_discount_percentage}%)</span>
                <span>- PKR {flatDiscount.toLocaleString()}</span>
              </div>
            )}
            {promoDiscount > 0 && (
              <div className="summary-row">
                <span>Promo Discount</span>
                <span>- PKR {promoDiscount.toLocaleString()}</span>
              </div>
            )}
            <div className="summary-row total">
              <span>Total</span>
              <span>PKR {total.toLocaleString()}</span>
            </div>

            <button onClick={() => navigate('/checkout')} className="checkout-btn">
              Proceed to Checkout
            </button>
            <Link to="/shop" className="continue-shopping-pill">Continue Shopping</Link>

            <p className="payment-note">Shipping cost is calculated at checkout based on your city · COD · Online Payment</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;